import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Table,
  Message,
  Label,
  Icon,
  Image,
  Button,
  Modal,
} from "semantic-ui-react";
import moment from "moment";
import { fineApi } from "@api/fine/fine";

const STATUS_CONFIG = {
  resolved: { color: "teal", icon: "checkmark" },
};

const STATUS_DISPLAY_NAMES = {
  resolved: "Resolved",
};

const DATE_FORMAT = "MMM DD, YYYY HH:mm";

const ReceiptImageModal = ({ imageUrl, open, onClose }) => {
  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header>
        <Icon name="file image" />
        Receipt Image
      </Modal.Header>
      <Modal.Content>
        <div style={{ textAlign: "center" }}>
          <Image src={imageUrl} fluid />
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Close</Button>
      </Modal.Actions>
    </Modal>
  );
};

ReceiptImageModal.propTypes = {
  imageUrl: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

const FineHistoryTable = ({ fineHistory }) => {
  const [fineDetails, setFineDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const fetchFineDetails = async () => {
      if (fineHistory.length === 0) {
        setLoading(false);
        return;
      }

      try {
        // Get unique fine IDs
        const fineIds = [
          ...new Set(fineHistory.map((h) => h.id || h.pid).filter(Boolean)),
        ];

        const response = await fineApi.getBatch(fineIds);

        // Build a map for easy lookup by ID
        const detailsMap = {};
        response.data.hits.forEach((fine) => {
          detailsMap[fine.id] = fine;
        });

        setFineDetails(detailsMap);
      } catch (error) {
        setFineDetails({});
      } finally {
        setLoading(false);
      }
    };

    fetchFineDetails();
  }, [fineHistory]);

  if (fineHistory.length === 0) {
    return (
      <Message info>
        <Message.Header>No Fine Records Found</Message.Header>
        <p>This patron has no fine management records to display.</p>
        <p>
          <em>This could mean:</em>
        </p>
        <ul>
          <li>No fines have been created for this patron</li>
          <li>All items were returned on time</li>
          <li>Fines were handled outside the fine management system</li>
        </ul>
      </Message>
    );
  }

  const getStatusConfig = (status) => {
    const key = status?.toLowerCase();
    return STATUS_CONFIG[key] || STATUS_CONFIG.default;
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "N/A";
    return `₱${Number(amount).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return moment(dateString).format(DATE_FORMAT);
  };

  const getFullImageUrl = (imageUrl) => {
    if (!imageUrl) return null;

    // If it's already a full URL, return as is
    if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
      return imageUrl;
    }

    // Use the environment variable for the API base URL
    const baseUrl = process.env.REACT_APP_INVENIO_REST_ENDPOINTS_BASE_URL;

    // If imageUrl starts with /fines/, construct the full URL
    if (imageUrl.startsWith("/fines/")) {
      // Remove leading slash and prepend base URL
      const cleanPath = imageUrl.substring(1);
      return `${baseUrl}/${cleanPath}`;
    }

    // If imageUrl doesn't have leading slash, add it
    const cleanPath = imageUrl.startsWith("/")
      ? imageUrl.substring(1)
      : imageUrl;
    return `${baseUrl}/${cleanPath}`;
  };

  const handleViewReceipt = (imageUrl) => {
    setSelectedReceipt(imageUrl);
    setModalOpen(true);
  };

  const renderReceiptColumn = (fine) => {
    const fineDetail = fineDetails[fine.id || fine.pid];
    const imageUrl = fineDetail?.image_url || fineDetail?.imageUrl;
    const fullImageUrl = getFullImageUrl(imageUrl);

    if (!fullImageUrl) {
      return (
        <div style={{ textAlign: "center", color: "#999" }}>
          <Icon name="file outline" />
          <div style={{ fontSize: "0.9em" }}>No Uploaded Receipt</div>
        </div>
      );
    }

    return (
      <div style={{ textAlign: "center" }}>
        <div style={{ marginBottom: "8px" }}>
          <Image
            src={fullImageUrl}
            size="small"
            centered
            style={{
              cursor: "pointer",
              border: "1px solid #ddd",
              borderRadius: "4px",
              maxHeight: "100px",
              objectFit: "cover",
            }}
            onClick={() => handleViewReceipt(fullImageUrl)}
            onError={(e) => {
              e.target.style.display = "none";
              e.target.parentElement.innerHTML =
                '<div style="color: #999; font-size: 0.9em;"><i class="icon file outline"></i><div>Image not found</div></div>';
            }}
          />
        </div>
        <Button
          size="mini"
          color="blue"
          onClick={() => handleViewReceipt(fullImageUrl)}
        >
          <Icon name="eye" />
          View
        </Button>
      </div>
    );
  };

  const renderDateInfo = (fine) => {
    const dateFields = [
      { key: "created_at", label: "Created", color: null },
      { key: "start_date", label: "Started", color: null },
      { key: "paid_date", label: "Paid", color: "green" },
      { key: "waived_date", label: "Waived", color: "blue" },
      { key: "resolved_date", label: "Resolved", color: "teal" },
    ];

    return (
      <div style={{ fontSize: "0.9em" }}>
        {dateFields.map(({ key, label, color }) => {
          if (!fine[key]) return null;
          return (
            <div key={key} style={color ? { color } : {}}>
              <strong>{label}:</strong> {formatDate(fine[key])}
            </div>
          );
        })}
        {fine.updated_at && fine.updated_at !== fine.created_at && (
          <div style={{ color: "#666" }}>
            <strong>Updated:</strong> {formatDate(fine.updated_at)}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Message icon>
        <Icon name="circle notched" loading />
        <Message.Content>
          <Message.Header>Loading Receipt Information</Message.Header>
          Please wait while we fetch the fine details...
        </Message.Content>
      </Message>
    );
  }

  return (
    <>
      <Table celled striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Fine ID</Table.HeaderCell>
            <Table.HeaderCell>Loan/Item Details</Table.HeaderCell>
            <Table.HeaderCell>Amount</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
            <Table.HeaderCell>Dates</Table.HeaderCell>
            <Table.HeaderCell>Receipt</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {fineHistory.map((fine) => {
            const statusConfig = getStatusConfig(fine.status);
            const fineDetail = fineDetails[fine.id || fine.pid];

            return (
              <Table.Row key={fine.id || fine.pid}>
                <Table.Cell>
                  <strong>{fine.id || fine.pid}</strong>
                  {fine.loan_id && (
                    <div style={{ fontSize: "0.9em", color: "#666" }}>
                      Loan: {fine.loan_id}
                    </div>
                  )}
                  {fineDetail?.or_number && (
                    <div
                      style={{
                        fontSize: "0.9em",
                        color: "#f4b952",
                        fontWeight: "bold",
                      }}
                    >
                      OR Number: {fineDetail.or_number}
                    </div>
                  )}
                </Table.Cell>

                <Table.Cell>
                  {fine.item_title && (
                    <div>
                      <strong>{fine.item_title}</strong>
                    </div>
                  )}
                  {fine.item_barcode && (
                    <div style={{ fontSize: "0.9em", color: "#666" }}>
                      Barcode: {fine.item_barcode}
                    </div>
                  )}
                  {fine.item_author && (
                    <div style={{ fontSize: "0.9em", color: "#666" }}>
                      Author: {fine.item_author}
                    </div>
                  )}
                </Table.Cell>

                <Table.Cell>
                  <div>
                    <strong>
                      {formatCurrency(fine.total_fine || fine.amount)}
                    </strong>
                  </div>
                  {fine.daily_rate && (
                    <div style={{ fontSize: "0.8em", color: "#666" }}>
                      Daily Rate: {formatCurrency(fine.daily_rate)}
                    </div>
                  )}
                  {fine.days_overdue && (
                    <div style={{ fontSize: "0.8em", color: "#666" }}>
                      Days Overdue: {fine.days_overdue}
                    </div>
                  )}
                  {fineDetail?.payment_reference && (
                    <div
                      style={{
                        fontSize: "0.8em",
                        color: "#666",
                        marginTop: "4px",
                      }}
                    >
                      Ref: {fineDetail.payment_reference}
                    </div>
                  )}
                </Table.Cell>

                <Table.Cell>
                  <Label color={statusConfig.color} size="small">
                    <Icon name={statusConfig.icon} />
                    {STATUS_DISPLAY_NAMES[fine.status?.toLowerCase()] ||
                      fine.status ||
                      "Unknown"}
                  </Label>
                </Table.Cell>

                <Table.Cell>{renderDateInfo(fine)}</Table.Cell>

                <Table.Cell>{renderReceiptColumn(fine)}</Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>

      {selectedReceipt && (
        <ReceiptImageModal
          imageUrl={selectedReceipt}
          open={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
};

FineHistoryTable.propTypes = {
  fineHistory: PropTypes.array.isRequired,
};

export default FineHistoryTable;
